import { useDataTableContext } from "@/components/DataTable/Context/DataTableContext";
import { User, BulkMessageSenderTypeEnum } from "@/__generated__/types";

interface FilterValue {
  value: string;
  user?: User;
  senderType?: BulkMessageSenderTypeEnum;
  renderComponent?: React.ComponentType<{
    value: string;
    user?: User;
    senderType?: BulkMessageSenderTypeEnum;
  }>;
}

interface CustomFilter {
  id: string;
  label: string;
  values: FilterValue[];
  avatarFilterLabel?: boolean;
}

export function useDataTableFilters(customFilters?: CustomFilter[]) {
  const { table } = useDataTableContext();

  if (!table)
    return {
      filtersToRender: [],
      handleFilterChange: () => undefined,
      handleResetFilters: () => undefined,
      currentFilter: null,
      disabled: true,
    };

  const getUniqueValuesWithAvatars = (columnId: string) => {
    const column = table.getColumn(columnId);
    if (!column) return [];

    const rows = table.getRowModel().rows;
    const uniqueValuesMap = new Map<
      string,
      { user: User; senderType: BulkMessageSenderTypeEnum }
    >();

    rows.forEach((row) => {
      const value = row.getValue(columnId) as string;
      if (value && !uniqueValuesMap.has(value)) {
        if (columnId === "sentFrom") {
          const original = row.original as {
            sentFrom: User;
            senderType: BulkMessageSenderTypeEnum;
          };
          const user = original.sentFrom;
          if (user) {
            uniqueValuesMap.set(value, {
              user,
              senderType: original.senderType,
            });
          }
        }
      }
    });

    return Array.from(uniqueValuesMap.entries());
  };

  const getDefaultFilters = (): CustomFilter[] => {
    const columns = table
      .getAllColumns()
      .filter(
        (column) =>
          column.columnDef.enableColumnFilter &&
          Array.from(column.getFacetedUniqueValues()?.entries() ?? []).length >
            1,
      );

    return columns.map((column) => {
      const meta = column.columnDef.meta as {
        label: string;
        avatarFilterLabel?: boolean;
        valueComponent?: React.ComponentType<{
          value: string;
          user?: User;
          senderType?: BulkMessageSenderTypeEnum;
        }>;
      };
      const uniqueValues = Array.from(
        column.getFacetedUniqueValues()?.entries() ?? [],
      );

      const values: FilterValue[] = uniqueValues.map(([value]) => {
        const baseValue = {
          value,
          renderComponent: meta.valueComponent,
        };
        if (meta.avatarFilterLabel) {
          const avatarData = getUniqueValuesWithAvatars(column.id).find(
            ([val]) => val === value,
          )?.[1];
          return avatarData ? { ...baseValue, ...avatarData } : baseValue;
        }
        return baseValue;
      });

      return {
        id: column.id,
        label: meta.label,
        values,
        avatarFilterLabel: meta.avatarFilterLabel,
      };
    });
  };

  const filtersToRender =
    customFilters && customFilters.length > 0
      ? customFilters
      : getDefaultFilters();

  const handleFilterChange = (columnId: string, value: string) => {
    const col = table.getColumn(columnId);
    if (!col) return;
    col.setFilterValue(value);
  };

  const handleResetFilters = () => table.resetColumnFilters();

  const currentFilter = table.getState().columnFilters[0];
  const hasActiveFilters = table.getState().columnFilters.length > 0;
  const hasData = table.getPageCount() > 0 || table.getRowCount() > 0;

  const disabled = !hasData && !hasActiveFilters;

  return {
    filtersToRender,
    handleFilterChange,
    handleResetFilters,
    currentFilter,
    disabled,
  };
}
